import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import { VocabularyWord } from '@/services/vocabularyService';

// === SESSION INTEGRATION ACTIONS ===
export const linkWordsToSessionRequest = createAction<{ sessionId: string; wordIds: string[] }>(
    'sessionIntegration/linkWordsToSessionRequest',
);
export const linkWordsToSessionSuccess = createAction<{ sessionId: string; wordIds: string[] }>(
    'sessionIntegration/linkWordsToSessionSuccess',
);
export const linkWordsToSessionFailure = createAction<string>('sessionIntegration/linkWordsToSessionFailure');

export const fetchSessionVocabularyRequest = createAction<string>('sessionIntegration/fetchSessionVocabularyRequest');
export const fetchSessionVocabularySuccess = createAction<VocabularyWord[]>(
    'sessionIntegration/fetchSessionVocabularySuccess',
);
export const fetchSessionVocabularyFailure = createAction<string>('sessionIntegration/fetchSessionVocabularyFailure');

export const updateSessionVocabularyProgressRequest = createAction<{
    sessionId: string;
    wordId: string;
    progress: {
        isCorrect: boolean;
        timeSpent: number;
        attempts: number;
    };
}>('sessionIntegration/updateSessionVocabularyProgressRequest');
export const updateSessionVocabularyProgressSuccess = createAction<{
    sessionId: string;
    wordId: string;
    progress: {
        isCorrect: boolean;
        timeSpent: number;
        attempts: number;
    };
}>('sessionIntegration/updateSessionVocabularyProgressSuccess');
export const updateSessionVocabularyProgressFailure = createAction<string>(
    'sessionIntegration/updateSessionVocabularyProgressFailure',
);

// === STATE INTERFACE ===
export interface SessionIntegrationState {
    sessionVocabulary: VocabularyWord[];

    // Loading States
    isLinkingWordsToSession: boolean;
    isLoadingSessionVocabulary: boolean;
    isUpdatingSessionProgress: boolean;

    // Error States
    sessionIntegrationError: string | null;
}

const initialState: SessionIntegrationState = {
    sessionVocabulary: [],
    isLinkingWordsToSession: false,
    isLoadingSessionVocabulary: false,
    isUpdatingSessionProgress: false,
    sessionIntegrationError: null,
};

const sessionIntegrationSlice = createSlice({
    name: 'sessionIntegration',
    initialState,
    reducers: {
        clearSessionIntegrationError: (state) => {
            state.sessionIntegrationError = null;
        },
        clearSessionVocabulary: (state) => {
            state.sessionVocabulary = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === LINK WORDS TO SESSION ===
            .addCase(linkWordsToSessionRequest, (state) => {
                state.isLinkingWordsToSession = true;
                state.sessionIntegrationError = null;
            })
            .addCase(linkWordsToSessionSuccess, (state) => {
                state.isLinkingWordsToSession = false;
            })
            .addCase(linkWordsToSessionFailure, (state, action) => {
                state.isLinkingWordsToSession = false;
                state.sessionIntegrationError = action.payload;
            })

            // === FETCH SESSION VOCABULARY ===
            .addCase(fetchSessionVocabularyRequest, (state) => {
                state.isLoadingSessionVocabulary = true;
                state.sessionIntegrationError = null;
            })
            .addCase(fetchSessionVocabularySuccess, (state, action) => {
                state.isLoadingSessionVocabulary = false;
                state.sessionVocabulary = action.payload;
            })
            .addCase(fetchSessionVocabularyFailure, (state, action) => {
                state.isLoadingSessionVocabulary = false;
                state.sessionIntegrationError = action.payload;
            })

            // === UPDATE SESSION VOCABULARY PROGRESS ===
            .addCase(updateSessionVocabularyProgressRequest, (state) => {
                state.isUpdatingSessionProgress = true;
                state.sessionIntegrationError = null;
            })
            .addCase(updateSessionVocabularyProgressSuccess, (state) => {
                state.isUpdatingSessionProgress = false;
            })
            .addCase(updateSessionVocabularyProgressFailure, (state, action) => {
                state.isUpdatingSessionProgress = false;
                state.sessionIntegrationError = action.payload;
            });
    },
});

export const { clearSessionIntegrationError, clearSessionVocabulary } = sessionIntegrationSlice.actions;

export default sessionIntegrationSlice.reducer;
